import React from 'react';
import { Check, Star, Zap, Crown, Rocket } from 'lucide-react';
import { MetaTags } from '../../components/seo/MetaTags';
import { StructuredData } from '../../components/seo/StructuredData';
import { PricingCard } from './components/PricingCard';
import { plans } from './data/plans';

export function PricingPage() {
  const pageTitle = 'Pricing Plans - Aviation Service Provider Solutions';
  const pageDescription = 'Choose the perfect plan for your aviation service business. Flexible pricing options for ground handling, maintenance, catering, and more.';
  const canonicalUrl = `${window.location.origin}/pricing`;

  const organizationData = {
    logo: 'https://airportal.com/logo.png',
    socialLinks: [
      'https://twitter.com/airportal',
      'https://linkedin.com/company/airportal'
    ]
  };

  // Reorder plans to show free at the bottom
  const mainPlans = plans.filter(plan => plan.id !== 'free');
  const freePlan = plans.find(plan => plan.id === 'free');

  const features = [
    {
      icon: Star,
      title: 'Premium Support',
      description: 'Get priority assistance from our aviation experts'
    },
    {
      icon: Zap,
      title: 'Instant Setup',
      description: 'Go live in minutes with our streamlined onboarding'
    },
    {
      icon: Crown,
      title: 'Verified Badge',
      description: 'Build trust with our verification system'
    }
  ];

  return (
    <>
      <MetaTags
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        keywords={[
          'aviation pricing',
          'airport service plans',
          'ground handling pricing',
          'aviation service provider'
        ]}
      />

      <StructuredData type="Organization" data={organizationData} />

      <main className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=2000')] bg-cover bg-center opacity-10"></div>
          
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 sm:w-80 sm:h-80 bg-primary-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 sm:w-80 sm:h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-white/80 text-sm font-medium mb-8">
                <Rocket className="w-4 h-4" />
                Trusted by 10,000+ Aviation Professionals
              </div>
              
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                <span className="block">Simple, Transparent</span>
                <span className="block bg-gradient-to-r from-primary-300 via-blue-300 to-primary-300 bg-clip-text text-transparent">
                  Pricing
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                Choose the plan that best fits your business needs. Scale your services with confidence 
                and grow your aviation business.
              </p>
            </div>

            {/* Features highlight */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {features.map((feature, index) => (
                <div key={index} className="group text-center">
                  <div className="inline-flex p-4 bg-gradient-to-r from-primary-500/20 to-blue-500/20 backdrop-blur-sm rounded-2xl mb-4 group-hover:from-primary-500/30 group-hover:to-blue-500/30 transition-all duration-300">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/70">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {/* Main pricing cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {mainPlans.map((plan) => (
              <div key={plan.id} className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-3xl blur-xl group-hover:from-white/10 group-hover:to-white/20 transition-all duration-300"></div>
                <div className={`
                  relative bg-white/5 backdrop-blur-xl rounded-3xl border overflow-hidden transition-all duration-300 hover:scale-105
                  ${plan.popular 
                    ? 'border-primary-500/50 shadow-2xl shadow-primary-500/25' 
                    : 'border-white/20 hover:border-white/30'
                  }
                `}>
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-primary-500 to-blue-600 text-white px-6 py-2 rounded-bl-2xl text-sm font-semibold">
                      Most Popular
                    </div>
                  )}

                  <div className="p-8 sm:p-10">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                      <div className="text-right">
                        <div className="flex items-baseline">
                          <span className="text-4xl font-bold text-white">${plan.price}</span>
                          <span className="ml-1 text-white/60">/mo</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-white/70 mb-8 leading-relaxed">{plan.description}</p>

                    <button className={`
                      w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 mb-8
                      ${plan.popular 
                        ? 'bg-gradient-to-r from-primary-500 to-blue-600 hover:from-primary-400 hover:to-blue-500 text-white shadow-lg hover:shadow-xl hover:scale-105' 
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30'
                      }
                    `}>
                      Get Started
                    </button>

                    <div className="space-y-4">
                      {plan.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-3">
                          <div className="flex-shrink-0 p-1 bg-gradient-to-r from-primary-500/20 to-blue-500/20 rounded-lg mt-0.5">
                            <Check className="h-4 w-4 text-primary-300" />
                          </div>
                          <span className="text-white/80 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Free plan */}
          {freePlan && (
            <div className="max-w-2xl mx-auto mb-16">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-semibold text-white mb-4">Start for Free</h2>
                <p className="text-white/70 text-lg">Get started with our basic features at no cost</p>
              </div>
              
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-3xl blur-xl"></div>
                <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/20 p-8 sm:p-10">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-white mb-2">{freePlan.name}</h3>
                    <div className="text-4xl font-bold text-white mb-4">$0<span className="text-lg text-white/60">/forever</span></div>
                    <p className="text-white/70">{freePlan.description}</p>
                  </div>

                  <button className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30 rounded-xl font-semibold transition-all duration-300 mb-8">
                    Start Free
                  </button>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {freePlan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <div className="flex-shrink-0 p-1 bg-gradient-to-r from-primary-500/20 to-blue-500/20 rounded-lg mt-0.5">
                          <Check className="h-4 w-4 text-primary-300" />
                        </div>
                        <span className="text-white/80 text-sm leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Enterprise CTA */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-blue-500/20 rounded-3xl blur-xl"></div>
            <div className="relative bg-gradient-to-r from-primary-500/10 to-blue-500/10 backdrop-blur-xl rounded-3xl border border-primary-500/30 p-8 sm:p-12 text-center">
              <div className="inline-flex p-4 bg-gradient-to-r from-primary-500 to-blue-600 rounded-2xl mb-6">
                <Crown className="h-8 w-8 text-white" />
              </div>
              
              <h2 className="text-3xl font-semibold text-white mb-4">
                Need something different?
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                Contact us for custom enterprise solutions tailored to your specific needs. 
                We'll work with you to create the perfect plan for your business.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:contact@airportals.net"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary-500 to-blue-600 hover:from-primary-400 hover:to-blue-500 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Contact Sales
                </a>
                <a
                  href="mailto:contact@airportals.net"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30 font-semibold rounded-xl transition-all duration-300"
                >
                  Schedule Demo
                </a>
              </div>
              
              <div className="mt-6 text-sm text-white/60">
                Or email us directly at: <a href="mailto:contact@airportals.net" className="text-primary-300 hover:text-primary-200 transition-colors">contact@airportals.net</a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}